import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Table,
  useToast,
  type TableRow,
} from '@citizen-walk/shared-ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { deleteAreaReportSnapshot } from './areaReportSnapshotActions';
import { buildAreaReportComparison } from './areaReportComparison';
import { AreaComparisonBarChart, REPORT_PALETTE } from './AreaComparisonBarChart';
import { AreaDonutChart } from './AreaDonutChart';
import { buildAreaDonuts } from './areaDonutSlices';
import { AreaComparisonMap } from './AreaComparisonMap';
import { SnapshotAggregationControls, type SnapshotChartMode } from './SnapshotAggregationControls';
import { formatPolarityCounts, type AxisGranularity } from './computeAreaAxisCounts';
import { POLARITY_LABELS } from '@citizen-walk/shared-types';
import { useRecomputedSnapshots } from './useRecomputedSnapshots';
import { useStreetSurveySelection } from './streetSurveySurveys';
import { StreetSurveySurveysPanel } from './StreetSurveySurveysPanel';
import { DEFAULT_STREET_SURVEY_KEY } from '@citizen-walk/shared-types';
import { useAreaReportSnapshots, type AreaReportSnapshotWithId } from './useAreaReportSnapshots';

/**
 * エリア選択集計 比較ダッシュボード (docs/25 §10-5)。
 * `/districts/:districtId/street-survey/area-reports` — `AreaReportModal` の「エリアとして
 * 保存」で作成したスナップショットの一覧・削除・複数選択時の表形式比較を行う。
 *
 * 比較は表形式 (ユーザー確認済み設計) — 複数系列の棒グラフは、本機能の前身である
 * `AreaBarChart`/PDFテンプレートの html2canvas レイアウト調整で長期間苦戦した経緯があり、
 * 確実さを優先して採用しない。
 */
function toDateOrNull(raw: unknown): Date | null {
  if (raw instanceof Date) return raw;
  if (raw && typeof (raw as { toDate?: () => Date }).toDate === 'function') {
    return (raw as { toDate: () => Date }).toDate();
  }
  return null;
}

export function AreaReportDashboardPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const districtState = useDistrict(districtId ?? null);
  const { state: authState } = useAdminAuth();
  const { toast } = useToast();

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canRun = isMaster || isDistrictAdminOfThis;
  // docs/25 §10-1: この地区で SV 机上調査が有効化されているか (StreetSurveyPage と同じゲート)。
  const featureEnabled =
    districtState.status === 'ready' && districtState.district.streetView?.analysisEnabled === true;

  const reportsState = useAreaReportSnapshots(districtId ?? null);
  // docs/98 R-97: 選んだ調査で保存した集計だけを比べる (調査の key の無い保存は既定の調査)。
  const surveySelection = useStreetSurveySelection(
    districtState.status === 'ready' ? districtState.district : null,
  );
  const reports = (reportsState.status === 'ready' ? reportsState.reports : []).filter(
    (r) => (r.surveyKey || DEFAULT_STREET_SURVEY_KEY) === surveySelection.surveyKey,
  );

  // 既定で全エリアを選ぶ (オーナー指示 2026-09-16)。null = まだ操作していない = 全件。
  const [selectedIdsState, setSelectedIds] = useState<string[] | null>(null);
  const selectedIds = selectedIdsState ?? reports.map((r) => r.id);
  const [deleteTarget, setDeleteTarget] = useState<AreaReportSnapshotWithId | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 比較のグラフ・表の集計項目を切り替える (docs/27 §9-3)。既定は保存時のまま。
  const [aggAxisKey, setAggAxisKey] = useState<string | null>(null);
  // 既定は細分類ごと (オーナー指示 2026-09-16)。保存時の集計は大分類のこともあるので、
  // 開いた時点で今の解析結果から集計し直す (「保存時の集計に戻す」で保存値を見られる)。
  const [granularity, setGranularity] = useState<AxisGranularity>('category');
  // 各項目を評価の区分ごとの棒に分ける (オーナー指示 2026-09-16)。
  // 既定で評価の積み上げ表示にする (オーナー指示 2026-09-16)。
  const [splitByPolarity, setSplitByPolarity] = useState(true);
  // グラフの表示形式 (2026-09-18 オーナー要望): 横棒の積み上げ / エリアごとの円。
  const [chartMode, setChartMode] = useState<SnapshotChartMode>('bar');
  const savedReports = reports.filter((r) => selectedIds.includes(r.id));
  // 集計項目・細かさを変えたときだけ、今の解析結果から集計し直す (評価で分ける表示は保存済みの
  // 内訳で足りるので読み込み不要)。既定は保存時の集計のまま = 地点を読み込まない。
  const recomputing = aggAxisKey !== null || granularity !== 'auto';
  const recomputed = useRecomputedSnapshots(districtId ?? null, savedReports, {
    enabled: recomputing,
    axisKey: aggAxisKey ?? null,
    granularity,
    surveyKey: surveySelection.surveyKey,
  });
  const selectedReports = recomputed.reports;
  const hasPolarity = selectedReports.some((r) => r.axisCounts.some((a) => a.polarity));
  const comparison = buildAreaReportComparison(selectedReports);

  function toggleSelect(id: string, checked: boolean) {
    const base = selectedIdsState ?? reports.map((r) => r.id);
    setSelectedIds(checked ? [...base, id] : base.filter((x) => x !== id));
  }

  async function handleDelete() {
    if (!deleteTarget || !districtId || deleting) return;
    setDeleting(true);
    try {
      await deleteAreaReportSnapshot(districtId, deleteTarget.id);
      toast('success', `「${deleteTarget.name}」を削除しました。`);
      setSelectedIds(
        (selectedIdsState ?? reports.map((r) => r.id)).filter((id) => id !== deleteTarget.id),
      );
    } catch (e) {
      toast('error', `削除に失敗しました: ${(e as Error).message}`);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  const listRows: TableRow[] = reports.map((r) => {
    const createdAt = toDateOrNull(r.createdAt);
    return {
      __rowKey: r.id,
      select: (
        <Checkbox
          label=""
          checked={selectedIds.includes(r.id)}
          onChange={(checked) => toggleSelect(r.id, checked)}
        />
      ),
      name: r.name,
      createdAt: createdAt ? createdAt.toLocaleString('ja-JP') : '—',
      pointCount: `${r.pointCount}件`,
      actions: (
        <Button size="sm" variant="danger" disabled={!canRun} onClick={() => setDeleteTarget(r)}>
          削除
        </Button>
      ),
    };
  });

  const polarityLabels = recomputed.evaluation?.polarityLabels ?? POLARITY_LABELS;
  const comparisonColumns = [
    // 列の見出しは選んだ調査項目の名前 (保存時の項目のままなら「項目」、docs/98 R-98 中)。
    {
      key: 'category',
      label:
        (aggAxisKey ? recomputed.axes.find((a) => a.key === aggAxisKey)?.label : null) ?? '項目',
    },
    ...selectedReports.map((r) => ({ key: r.id, label: r.name })),
  ];
  const comparisonRows: TableRow[] = [
    {
      __rowKey: '__pointCount',
      category: '地点数',
      ...Object.fromEntries(selectedReports.map((r) => [r.id, `${r.pointCount}件`])),
    },
    // docs/27 §9: 評価の内訳 (評価軸を持つテンプレートで保存したスナップショットのみ値が入る)。
    ...(selectedReports.some((r) => r.evaluation)
      ? [
          {
            __rowKey: '__evaluation',
            category: `評価（${polarityLabels.positive} / ${polarityLabels.negative} / ${polarityLabels.neutral}）`,
            ...Object.fromEntries(
              selectedReports.map((r) => [
                r.id,
                r.evaluation ? formatPolarityCounts(r.evaluation, polarityLabels) : '—',
              ]),
            ),
          },
        ]
      : []),
    ...comparison.rows.map((row) => ({
      __rowKey: row.key,
      category: (
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: row.color }}
          />
          {row.label}
        </span>
      ),
      // 地点数の異なるエリア同士を生の件数で比較すると誤解を招くため (地点数が多いエリアほど
      // 単純に件数も増える)、1地点あたり平均件数を主表示にし、生の件数は括弧書きで参考表示する。
      ...Object.fromEntries(
        row.values.map((v, i) => [
          selectedReports[i]!.id,
          `${row.perPointValues[i]!.toFixed(1)}件/地点 (${v}件)`,
        ]),
      ),
    })),
  ];

  const pageHeader = (
    <header className="mb-6">
      <Link
        to={
          districtId
            ? `/districts/${districtId}/street-survey${surveySelection.querySuffix}`
            : '/districts'
        }
        className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
      >
        ← ストリートビュー机上調査
      </Link>
      <h1 className="text-2xl font-bold text-slate-900 mt-2">
        エリア比較ダッシュボード
        {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
      </h1>
      <p className="text-sm text-slate-500 mt-1">
        「エリア選択集計」で保存したスナップショットを一覧・比較します。2件以上選択すると表形式で比較できます。
      </p>
      {districtId && (
        <div className="mt-3">
          <StreetSurveySurveysPanel
            districtId={districtId}
            district={districtState.status === 'ready' ? districtState.district : null}
            selection={surveySelection}
            canManage={false}
            uid={null}
          />
        </div>
      )}
    </header>
  );

  // docs/25 §10-1: 未有効化の地区では机上調査そのものを出さない (StreetSurveyPage と揃える)。
  if (districtState.status === 'ready' && !featureEnabled) {
    return (
      <>
        {pageHeader}
        <Card padding="md" className="border-amber-300 bg-amber-50">
          <p className="text-sm text-amber-800">
            この地区ではストリートビュー机上調査を利用できません。
            {isMaster
              ? '「AI 設定」ページの「ストリートビュー机上調査」から、この地区で実施可能にできます。'
              : '利用するにはマスター管理者による有効化が必要です。'}
          </p>
        </Card>
      </>
    );
  }

  return (
    <>
      {pageHeader}

      {!canRun && (
        <Card padding="md" className="border-amber-300 bg-amber-50 mb-4">
          <p className="text-sm text-amber-800">
            この地区での実行権限がありません。結果の閲覧のみ可能です。
          </p>
        </Card>
      )}

      <Card padding="md" className="mb-4">
        <Table
          columns={[
            { key: 'select', label: '選択', className: 'w-12' },
            { key: 'name', label: 'エリア名' },
            { key: 'createdAt', label: '保存日時' },
            { key: 'pointCount', label: '地点数' },
            { key: 'actions', label: '' },
          ]}
          rows={listRows}
          emptyMessage="保存済みのエリア選択集計がありません。「エリア選択集計」画面の「エリアとして保存」から作成してください。"
        />
      </Card>

      {/* 2026-09-18: 1 件でも表示する (エリアごとの構成を見る・集計項目を変える用途があるため。
          以前は 2 件以上の選択でしか集計の切り替えもグラフも出せなかった)。 */}
      {selectedReports.length >= 1 && (
        <Card padding="md" className="mb-4">
          <h2 className="text-sm font-bold text-slate-700 mb-2">
            {selectedReports.length >= 2
              ? `比較（${selectedReports.length}件選択中）`
              : `エリアの内訳（${selectedReports[0]?.name ?? ''}）`}
          </h2>
          <SnapshotAggregationControls
            axes={recomputed.axes}
            axisKey={aggAxisKey}
            onAxisKeyChange={setAggAxisKey}
            granularity={granularity}
            onGranularityChange={setGranularity}
            splitByPolarity={splitByPolarity}
            onSplitByPolarityChange={setSplitByPolarity}
            hasPolarity={hasPolarity}
            recomputing={recomputing}
            onResetToSaved={() => {
              setAggAxisKey(null);
              setGranularity('auto');
            }}
            loading={recomputed.loading}
            missingPointCount={recomputed.missingPointCount}
            chartMode={chartMode}
            onChartModeChange={setChartMode}
          />
          <p className="text-xs text-slate-500 mb-2">
            選択したエリアの範囲（エリアごとに個別の地図）
          </p>
          <AreaComparisonMap
            areas={selectedReports.map((r, i) => ({
              id: r.id,
              name: r.name,
              polygon: r.polygon,
              color: REPORT_PALETTE[i % REPORT_PALETTE.length]!,
            }))}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4"
          />
          <p className="text-xs text-slate-500 mb-2">
            {chartMode === 'donut'
              ? 'エリアごとの内訳（1地点あたり平均検出数の構成比）'
              : '1地点あたり平均検出数（地点数が異なるエリア同士でも公平に比較できるよう正規化）'}
          </p>
          {chartMode === 'donut' ? (
            <AreaDonutChart
              donuts={buildAreaDonuts({
                rows: comparison.rows,
                areas: selectedReports.map((r, i) => ({
                  id: r.id,
                  name: r.name,
                  color: REPORT_PALETTE[i % REPORT_PALETTE.length]!,
                  pointCount: r.pointCount,
                })),
                splitByPolarity,
                polarityLabels,
              })}
              polarityLabels={polarityLabels}
              splitByPolarity={splitByPolarity}
            />
          ) : (
            <AreaComparisonBarChart
              polarityLabels={polarityLabels}
              rows={comparison.rows.map((row) => ({
                ...row,
                values: row.perPointValues,
                // 評価の内訳も 1 地点あたりに正規化して積み上げる。
                polarities: row.polarities.map((p, i) => {
                  const pointCount = selectedReports[i]?.pointCount ?? 0;
                  if (!p || pointCount <= 0) return null;
                  return {
                    positive: p.positive / pointCount,
                    negative: p.negative / pointCount,
                    neutral: p.neutral / pointCount,
                  };
                }),
              }))}
              reportNames={selectedReports.map((r) => r.name)}
              stackByPolarity={splitByPolarity}
              unit="件/地点"
            />
          )}

          <h3 className="text-sm font-bold text-slate-700 mt-5 mb-2">詳細（表）</h3>
          <Table columns={comparisonColumns} rows={comparisonRows} />

          <h3 className="text-sm font-bold text-slate-700 mt-5 mb-2">AI要約</h3>
          <div className="grid grid-cols-1 gap-2">
            {selectedReports.map((r) => (
              <div key={r.id} className="rounded border border-slate-200 px-3 py-2">
                <p className="text-xs font-semibold text-slate-600 mb-1">{r.name}</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {r.summary ?? '（要約なし）'}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        intent="danger"
        title="エリアを削除しますか？"
        message={`「${deleteTarget?.name ?? ''}」の保存済みスナップショットを完全に削除します。復元できません。`}
        confirmLabel={deleting ? '削除中…' : '削除する'}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
